'use client';
import { useState } from 'react';
import { useDesign } from '@/context/DesignContext';
import { getImageUrl } from '@/lib/utils';

interface Product {
  id?: string;
  name: string;
  description?: string | null;
  image: string;
  price: number;
  stock: number;
  tag?: string;
  category?: { id: number; name: string } | null;
}

interface ProductsProps {
  products?: Product[];
  title?: string;
  design?: any;
}

export default function Products({ products, title, design: propDesign }: ProductsProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;
  const [activeProduct, setActiveProduct] = useState<string | null>(null);

  if (!products || products.length === 0) return null;

  const primaryColor = design?.colors?.primary || '#C8A96E';
  const textColor = design?.colors?.text || '#F5F5F5';
  const textLightColor = design?.colors?.textLight || '#A3A3A3';
  const secondaryColor = design?.colors?.secondary || '#0F0F0F';
  const headingFont = design?.typography?.headingFont || "'Cormorant Garamond', serif";
  const bodyFont = design?.typography?.bodyFont || "'Inter', sans-serif";
  const borderRadius = design?.effects?.borderRadius || '2px';

  return (
    <section className="px-8 md:px-16 py-24" style={{ background: `${primaryColor}05` }}>
      <div className="flex items-end justify-between mb-12">
        <div>
          <div className="text-xs uppercase tracking-widest mb-3" style={{ fontFamily: bodyFont, color: primaryColor }}>— Featured</div>
          {title && <h2 className="font-serif text-5xl md:text-6xl font-light" style={{ fontFamily: headingFont, color: textColor }}>{title}</h2>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {products.map((p) => (
          <div
            key={p.id || p.name}
            className="group"
            onMouseEnter={() => setActiveProduct(p.id || p.name)}
            onMouseLeave={() => setActiveProduct(null)}
          >
            <div className="relative mb-4" style={{ height: 380, borderRadius, background: secondaryColor }}>
              {p.image ? (
                <img
                  src={getImageUrl(p.image)}
                  alt={p.name}
                  className="w-full h-full object-cover"
                  style={{ borderRadius }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ color: textLightColor }}>
                  <span>No image</span>
                </div>
              )}
              {p.tag && (
                <div className="absolute top-4 left-4 px-2 py-1 text-xs uppercase tracking-widest font-light" style={{ background: primaryColor, color: secondaryColor }}>{p.tag}</div>
              )}
              {p.stock !== undefined && (
                <div className="absolute bottom-4 right-4 px-2 py-1 text-xs uppercase tracking-widest font-light" style={{ background: p.stock > 0 ? '#10B981' : '#EF4444', color: '#FFFFFF' }}>
                  {p.stock > 0 ? `En stock (${p.stock})` : 'Rupture'}
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm uppercase tracking-widest font-light" style={{ fontFamily: bodyFont, color: textColor }}>{p.name}</div>
                  {p.category && (
                    <div className="text-xs tracking-wider mt-1" style={{ color: textLightColor }}>{p.category.name}</div>
                  )}
                </div>
                <div className="font-serif text-lg font-light" style={{ fontFamily: headingFont, color: primaryColor }}>{p.price} TND</div>
              </div>
              {p.description && (
                <p className="text-sm mt-2 line-clamp-2" style={{ color: textLightColor, fontFamily: bodyFont }}>{p.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}